import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { InfoIcon } from 'lucide-react';
import { FinanceCircular, Vehicle } from '../../types/catalog';
import { OfferContext, circularToSimulation } from '../../utils/finance';
import { bestCircularQuote, circularToOffer, eligibleLines, maxFinanceable, quoteCircular, rateTypeLabel } from '../../utils/circular';
import { formatARS, formatDate, formatPct } from '../../utils/format';
import { CircularTable } from './CircularTable';
import { OfferStatus } from '../OfferStatus';
import { Modal } from '../ui/Modal';
import { InquiryForm } from '../InquiryForm';
interface CircularSimulatorProps {
  vehicle: Vehicle;
  circular: FinanceCircular;
}
const STEP = 100000;
export function CircularSimulator({
  vehicle,
  circular
}: CircularSimulatorProps) {
  const lines = useMemo(() => eligibleLines(circular, vehicle), [circular, vehicle]);
  const best = useMemo(() => bestCircularQuote(circular, vehicle), [circular, vehicle]);
  const [lineId, setLineId] = useState(best?.line.id ?? lines[0]?.id ?? '');
  const [term, setTerm] = useState(best?.row.term ?? lines[0]?.terms[0]?.term ?? 0);
  const [financed, setFinanced] = useState(best?.financed ?? 0);
  const [askOpen, setAskOpen] = useState(false);
  const line = lines.find(l => l.id === lineId) ?? lines[0];
  const row = line?.terms.find(t => t.term === term) ?? line?.terms[line.terms.length - 1];
  const cap = line ? maxFinanceable(line, vehicle.price) : 0;

  // Al cambiar de unidad, vuelve a la línea recomendada.
  useEffect(() => {
    if (!best) return;
    setLineId(best.line.id);
    setTerm(best.row.term);
    setFinanced(best.financed);
  }, [vehicle.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectLine = (id: string, t?: number) => {
    const next = lines.find(l => l.id === id);
    if (!next) return;
    setLineId(id);
    const nextTerm = t ?? (next.terms.some(x => x.term === term) ? term : next.terms[next.terms.length - 1].term);
    setTerm(nextTerm);
    setFinanced(maxFinanceable(next, vehicle.price));
  };
  if (!line || !row) {
    return <div className="rounded-2xl border border-dashed border-line p-10 text-center text-sm text-mist" data-id="element-978" data-mp="29oh1fg5fl0nx">Esta unidad no tiene líneas habilitadas en la circular vigente.</div>;
  }
  const q = quoteCircular(circular, line, row, vehicle.price, financed);
  const ctx: OfferContext = {
    offer: circularToOffer(q),
    source: 'marca',
    circular: q
  };
  const sim = circularToSimulation(q);
  const isUva = line.rateType === 'uva';
  const summary: [string, string, boolean?][] = [['Precio del vehículo', formatARS(q.price)], ['Monto a financiar', formatARS(q.financed)], ['Anticipo (diferencia)', formatARS(q.down)], [`Gestión crediticia y gastos de otorgamiento (${circular.managementFeePct}%)`, formatARS(q.managementFee)], ['Total a abonar al inicio', formatARS(q.upfront), true], ['TNA', formatPct(row.tna)], ['CFT', 'No informado por la entidad'], ['Cuota cada $1.000', `$${row.installmentPer1000.toLocaleString('es-AR', {
    minimumFractionDigits: 2
  })}`], ['Total en cuotas', formatARS(q.totalInstallments)], ['Costo total estimado', formatARS(q.totalCost), true]];
  return <div className="grid gap-6 lg:grid-cols-[1fr_420px]" data-id="element-979" data-mp="3sog8230dugug">
      <div className="space-y-6" data-id="element-980" data-mp="1f0safka71y7">
        <div className="card flex items-center gap-4 p-4" data-id="element-981" data-mp="1i52puocatxb">
          <img src={vehicle.images[0]} alt="" className="h-16 w-24 rounded-lg object-cover" data-id="element-982" data-mp="2zyr8zv78gqvh" />
          <div className="min-w-0 flex-1" data-id="element-983" data-mp="34vzil7ebfml4">
            <p className="truncate font-semibold" data-id="element-984" data-mp="34yve6p2oxz6e">{vehicle.model} {vehicle.version}</p>
            <p className="text-sm text-mist" data-id="element-985" data-mp="39ukgh063hu6e">Precio de contado {formatARS(vehicle.price)}</p>
          </div>
          <Link to={`/vehiculo/${vehicle.id}`} className="hidden text-sm text-neon hover:text-neon-hi sm:block" data-id="element-986" data-mp="3esvqpc0ay6p5">Ver detalle</Link>
        </div>

        <fieldset className="card p-5" data-id="element-987" data-mp="u47ze2xswzv2">
          <legend className="sr-only" data-id="element-988" data-mp="u36df3f8ysyk">Línea de financiación</legend>
          <p className="text-sm font-semibold" data-id="element-989" data-mp="rpb08vpzwu6a">Elegí la línea de financiación</p>
          <p className="mt-1 text-xs text-mist" data-id="element-990" data-mp="nor4nwl2lym9">{circular.lender} · {circular.reference}</p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-991" data-mp="11nz48ql5b8qz">
            {lines.map(l => {
            const active = l.id === line.id;
            const minCoef = Math.min(...l.terms.map(t => t.tna));
            return <label key={l.id} className={`flex cursor-pointer flex-col rounded-xl border p-3.5 transition-colors duration-150 ${active ? 'border-neon bg-neon/10' : 'border-line hover:border-neon/50'}`} data-id="element-992" data-mp="1anvp0h4ezngr">
                  <input type="radio" name="circular-line" className="sr-only" checked={active} onChange={() => selectLine(l.id)} data-id="element-993" data-mp="1kdf89nw1hjc5" />
                  <span className="flex items-start justify-between gap-2" data-id="element-994" data-mp="1fhzu0uu4dlf7">
                    <span className="font-semibold leading-tight" data-id="element-995" data-mp="1udfvvm0xylm5">{l.name}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${l.rateType === 'uva' ? 'bg-warn/15 text-warn' : 'bg-neon/15 text-neon-hi'}`} data-id="element-996" data-mp="1pf4ix2fcnuzp">{rateTypeLabel(l)}</span>
                  </span>
                  <span className="mt-0.5 text-xs text-mist" data-id="element-997" data-mp="2d0f77h5o2jfb">{l.detail}</span>
                  <span className="mt-2 text-sm" data-id="element-998" data-mp="1z0nleu8wryqr">Hasta <strong data-id="element-999" data-mp="1z3jo0vvpbblj">{formatARS(l.maxAmount)}</strong></span>
                  <span className="text-xs text-mist" data-id="element-1000" data-mp="23z8l8sk90geo">
                    {l.terms[0].term} a {l.terms[l.terms.length - 1].term} meses · TNA desde {formatPct(minCoef)}
                  </span>
                </label>;
          })}
          </div>
          {line.audience === 'taxi-remis' && <p className="mt-3 rounded-lg bg-warn/10 px-3 py-2 text-xs text-warn" data-id="element-1001" data-mp="1gcip27ukzg1z">Línea exclusiva para taxis y remises con habilitación vigente.</p>}
        </fieldset>

        <div className="card space-y-6 p-5" data-id="element-1002" data-mp="16dt4u8f7pjik">
          <fieldset data-id="element-1003" data-mp="16f47u60u2n8m">
            <legend className="text-sm font-semibold" data-id="element-1004" data-mp="1bbl6hu5nsjzy">Plazo</legend>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-1005" data-mp="1bdyyfu51lg3a">
              {line.terms.map(t => <label key={t.term} className={`cursor-pointer rounded-xl border px-4 py-2 text-sm transition-colors duration-150 ${term === t.term ? 'border-neon bg-neon/10 text-white' : 'border-line text-mist hover:text-white'}`} data-id="element-1006" data-mp="s9cdo39xk7bx">
                  <input type="radio" name="circular-term" className="sr-only" checked={term === t.term} onChange={() => setTerm(t.term)} data-id="element-1007" data-mp="dilgsgthei1y" />
                  <span className="block font-semibold" data-id="element-1008" data-mp="1b5lmab22c5oj">{t.term} meses</span>
                  <span className={`block text-[11px] ${t.tna === 0 ? 'text-neon-hi' : ''}`} data-id="element-1009" data-mp="166r2p6w9ud3d">TNA {formatPct(t.tna)}</span>
                </label>)}
            </div>
          </fieldset>

          <div data-id="element-1010" data-mp="w9mk1h1mwzrb">
            <div className="flex items-baseline justify-between gap-3" data-id="element-1011" data-mp="w81mrjnxr2pr">
              <label htmlFor="circ-financed" className="text-sm font-semibold" data-id="element-1012" data-mp="3lyf176qxodoa">Monto a financiar</label>
              <span className="text-xs text-mist" data-id="element-1013" data-mp="3gysm0rrjoncu">Máximo {formatARS(cap)}</span>
            </div>
            <input id="circ-financed" type="range" min={Math.min(STEP * 10, cap)} max={cap} step={STEP} value={q.financed} onChange={e => setFinanced(Number(e.target.value))} className="mt-3 w-full accent-[#1FA8FF]" data-id="element-1014" data-mp="1whsww79p0qp2" />
            <p className="mt-2 font-display text-2xl font-bold" data-id="element-1015" data-mp="2b78o1od4yy5a">{formatARS(q.financed)}</p>
            <p className="text-xs text-mist" data-id="element-1016" data-mp="2g4qwldn8rbcr">El resto del precio ({formatARS(q.down)}) se abona como anticipo.</p>
          </div>
        </div>

        <details className="card group p-5" data-id="element-1017" data-mp="2p5yjkzkb4prj">
          <summary className="cursor-pointer list-none text-sm font-semibold marker:hidden" data-id="element-1018" data-mp="2p689yo99kyxr">
            Ver todas las líneas de la circular para este modelo
          </summary>
          <div className="mt-4" data-id="element-1019" data-mp="usph3rq6i4is">
            <CircularTable circular={circular} lines={lines} selected={{
            lineId: line.id,
            term: row.term
          }} onSelect={(id, t) => selectLine(id, t)} data-id="element-1020" data-mp="utidbyrihbmo" />
          </div>
        </details>
      </div>

      <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1021" data-mp="31mhpstij82eg">
        <div className="card overflow-hidden" aria-live="polite" data-id="element-1022" data-mp="2wpzpc5u8iq7x">
          <div className="border-b border-line bg-panel p-5" data-id="element-1023" data-mp="3be49le8xr9st">
            <p className="text-xs text-mist" data-id="element-1024" data-mp="3bdks1yn76nfb">{isUva ? 'Cuota inicial estimada (en UVA)' : 'Cuota mensual fija'}</p>
            <p className="font-display text-4xl font-black tracking-tight text-neon" data-id="element-1025" data-mp="36h435286c516">{formatARS(q.installment)}</p>
            <p className="mt-1 text-sm text-mist" data-id="element-1026" data-mp="2ir9p4hc4cafc">{row.term} cuotas · {line.name}</p>
            <div className="mt-3" data-id="element-1027" data-mp="2dsgd6tchcrha"><OfferStatus offer={ctx.offer} source="marca" data-id="element-1028" data-mp="2snml5lv4z0d9" /></div>
          </div>
          <dl className="divide-y divide-line px-5 text-sm" data-id="element-1029" data-mp="2sljk86sg3mtr">
            {summary.map(([k, v, strong]) => <div key={k} className="flex justify-between gap-4 py-2.5" data-id="element-1030" data-mp="2nqw1kok4hmn3">
                <dt className="text-mist" data-id="element-1031" data-mp="7vkfs7alzvvw">{k}</dt>
                <dd className={`shrink-0 text-right ${v.startsWith('No informado') ? 'italic text-mist/70' : 'font-medium'} ${strong ? 'font-bold text-white' : ''}`} data-id="element-1032" data-mp="2yu19oegd8cl">{v}</dd>
              </div>)}
          </dl>
          <div className="space-y-2 p-5 pt-3" data-id="element-1033" data-mp="fhxx98zpbagw">
            <button onClick={() => setAskOpen(true)} className="btn btn-primary w-full" data-id="element-1034" data-mp="fejysbu5rleq">Solicitar asesoramiento con esta simulación</button>
            <Link to="/chequeo" className="btn btn-ghost w-full" data-id="element-1035" data-mp="5hytdpe888ad">Chequeo crediticio</Link>
          </div>
          <div className="flex gap-2 border-t border-line p-5 text-[11px] leading-relaxed text-mist" data-id="element-1036" data-mp="t9ciznkztprn">
            <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden data-id="element-1037" data-mp="y6cq81tkwykc" />
            <p data-id="element-1038" data-mp="kgj2xnxq2mcq">
              Condiciones de la {circular.reference} de {circular.lender}, vigentes hasta el {formatDate(circular.validUntil)}. Cuota con IVA incluido, calculada con el coeficiente oficial cada $1.000.{' '}
              {isUva && 'En la línea UVA la cuota se expresa en UVA y se actualiza mensualmente según su cotización. '}
              Los gastos de gestión crediticia y otorgamiento son un promedio estimado del {circular.managementFeePct}% del capital financiado; el monto final se confirma al aprobar el crédito.{' '}
              {circular.notes.join(' ')}
            </p>
          </div>
        </div>
      </aside>

      <Modal open={askOpen} onClose={() => setAskOpen(false)} title="Solicitar asesoramiento" data-id="element-1039" data-mp="5oy6w0238e7k">
        <InquiryForm channel="simulador" vehicle={vehicle} simulation={{
        sim,
        ctx
      }} idPrefix="circ" data-id="element-1040" data-mp="amzqcm3fefjk" />
      </Modal>
    </div>;
}